// SELECT-body indexing pipeline — Phase-2 Slices 18 ("views") + 19 ("inline
// TVFs") + 20 ("scalar SQL functions").
//
// One entry point per parseable kind whose body resolves to a SELECT. Each
// peels off its dialect-specific header (`viewPrefix.ts` / `inlineTvfPrefix.ts`
// / `scalarFuncPrefix.ts`) and hands the inner SELECT to the shared
// `parseAndWalk`. `rangeFromOffsets` against the ORIGINAL body re-derives
// accurate `(startLine, startColumn)` so consumers can slice the original
// `bodyText` by `range.startOffset` / `endOffset`.
//
// These are SINGLE-OBJECT functions on purpose: `indexBuilder.ts` drives them
// from its time-sliced walk, and Slice 22's DDL invalidator calls them
// directly to re-index one object after an `ALTER VIEW` / `ALTER FUNCTION`.
// `indexBuilder.ts` re-exports all three so existing import paths still work.
//
// Parse failure is non-fatal: a body the parser rejects emits one
// `parseStatus: 'parse-failed'` record spanning the whole body so Slice 27's
// rename planner can refuse to rewrite it.

import { buildParsedDocumentFromSource, getDocumentStructure } from '../../sql-grammar/document';
import { buildLineIndex, rangeFromOffsets, type LineIndex } from '../../sql-grammar/document/lineIndex';
import type { ParseOptions, SqlEngine } from '../../sql-grammar/model';
import { extractQueryStructure } from '../../sql-resolver/structure/extractQueryStructure';
import type { ParsedBuffer, QueryStructure } from '../../sql-resolver/types';

import type { IdentifierIndex, IdentifierKind, ReferenceRecord } from '../types';

import type { PersistedObjectListing } from './objectBodyFetcher';
import { stripInlineTvfPrefix } from './inlineTvfPrefix';
import { stripScalarFuncPrefix } from './scalarFuncPrefix';
import { stripCreateViewPrefix } from './viewPrefix';
import { walkSelect } from './viewWalker';
import { emitAliasQualifiedColumnRefs, emitBareColumnRefs } from './columnRefScanner';

/** Parse a single view body and emit reference records. Exported so Slice 22's
 *  DDL invalidator can re-index a single view after `ALTER VIEW`. */
export function indexViewListing(
  listing: PersistedObjectListing,
  index: IdentifierIndex,
): void {
  const lineIdx = buildLineIndex(listing.bodyText);
  const { selectSource, byteOffset } = stripCreateViewPrefix(listing.bodyText);
  parseAndWalk(listing, selectSource, byteOffset, lineIdx, 'view', index);
}

/** Parse a single inline-TVF body and emit reference records. Same pattern as
 *  `indexViewListing` but uses `stripInlineTvfPrefix` to peel off the
 *  `CREATE FUNCTION` header before parsing the inner SELECT. */
export function indexInlineTvfListing(
  listing: PersistedObjectListing,
  index: IdentifierIndex,
): void {
  const lineIdx = buildLineIndex(listing.bodyText);
  const { selectSource, byteOffset } = stripInlineTvfPrefix(
    listing.bodyText,
    listing.parseOptions.engine,
  );
  parseAndWalk(listing, selectSource, byteOffset, lineIdx, 'routine', index);
}

/** Parse a single scalar SQL function body and emit reference records. Same
 *  pattern as `indexInlineTvfListing` but uses `stripScalarFuncPrefix` to
 *  handle the dialect-specific scalar-return shape. Multi-statement procedural
 *  bodies fall through to parse-failed (covered by the four ANTLR procedural
 *  grammars in NAV3). */
export function indexScalarFunctionListing(
  listing: PersistedObjectListing,
  index: IdentifierIndex,
): void {
  const lineIdx = buildLineIndex(listing.bodyText);
  const { selectSource, byteOffset } = stripScalarFuncPrefix(
    listing.bodyText,
    listing.parseOptions.engine,
  );
  parseAndWalk(listing, selectSource, byteOffset, lineIdx, 'routine', index);
}

/** Shared parse + walk pipeline for any parseable-object kind that resolves to
 *  a SELECT body. `parseFailedKind` is the `IdentifierKind` recorded on the
 *  parse-failed placeholder ('view' for views, 'routine' for inline-TVFs). */
function parseAndWalk(
  listing: PersistedObjectListing,
  selectSource: string,
  byteOffset: number,
  lineIdx: LineIndex,
  parseFailedKind: IdentifierKind,
  index: IdentifierIndex,
): void {
  const engine = listing.parseOptions.engine;
  const parseOptions: ParseOptions = {
    engine,
    identifierFolding: defaultFoldingForEngine(engine),
  };

  let buffer: ParsedBuffer;
  try {
    const parsedDocument = buildParsedDocumentFromSource(selectSource, parseOptions);
    const documentStructure = getDocumentStructure(parsedDocument, listingDocumentId(listing));
    buffer = { parsedDocument, documentStructure };
  } catch {
    recordParseFailed(listing, lineIdx, parseFailedKind, index);
    return;
  }

  const selectStmts = buffer.documentStructure.statements.filter((s) => s.kind === 'select');
  if (selectStmts.length === 0) {
    recordParseFailed(listing, lineIdx, parseFailedKind, index);
    return;
  }

  for (const stmt of selectStmts) {
    let qs: QueryStructure;
    try {
      qs = extractQueryStructure(buffer, stmt);
    } catch {
      continue;
    }
    walkSelect(qs, listing, byteOffset, lineIdx, index);
    // Also scan the body for `<alias>.<column>` token pairs. The structural
    // extractor's select-list splitter trips on engine-specific quoted
    // identifiers (e.g. MySQL backticked aliases), so this fallback ensures
    // column refs land regardless of select-list parsing fidelity. The bare-
    // ref scanner runs after with the same dedupe Set so a co-located ref the
    // alias scanner already emitted is not re-recorded.
    const alreadyEmitted = new Set<string>();
    emitAliasQualifiedColumnRefs(qs, selectSource, parseOptions, listing, byteOffset, lineIdx, index, alreadyEmitted);
    emitBareColumnRefs(qs, selectSource, parseOptions, listing, byteOffset, lineIdx, index, alreadyEmitted);
  }
}

function listingDocumentId(listing: PersistedObjectListing): string {
  const { foldedSchema, foldedName } = listing.objectName;
  return `${listing.objectKind}::${foldedSchema ?? ''}::${foldedName}`;
}

function defaultFoldingForEngine(engine: SqlEngine): ParseOptions['identifierFolding'] {
  if (engine === 'postgres') return 'lower';
  if (engine === 'oracle') return 'upper';
  return 'case-insensitive';
}

function recordParseFailed(
  listing: PersistedObjectListing,
  lineIdx: LineIndex,
  identifierKind: IdentifierKind,
  index: IdentifierIndex,
): void {
  const range = rangeFromOffsets(lineIdx, 0, listing.bodyText.length);
  const record: ReferenceRecord = {
    objectKind: listing.objectKind,
    objectName: listing.objectName,
    identifier: listing.objectName,
    identifierKind,
    range,
    referenceKind: 'read',
    parseStatus: 'parse-failed',
  };
  index.record(record);
}
